import { optionalOutcome, participationOutcome } from './grading-mode.js';
import { resolveMatchedBranch, activeFeedbackForBranch } from './dropdown.js';
import { pickScenarioBranch, resolveScenarioRoles, SCALE_VALUE_TRIGGER_PREFIX } from './scenario.js';
import { addVersionWithRoles, applyVersionHead, pickVersion, removeVersionWith, versionRolesBy, writeVersionWithRoles } from './versions.js';
export const SCALE_MIN_VALUES = 2;
export const SCALE_MAX_VALUES = 15;
const VALUE_PREFIX = SCALE_VALUE_TRIGGER_PREFIX;
/** Trigger for one specific point. */
export function buildScaleValueTrigger(value) {
    return `${VALUE_PREFIX}${value}`;
}
/** Returns null if `trigger` isn't a per-value Scale trigger. */
export function parseScaleValueTrigger(trigger) {
    if (!trigger.startsWith(VALUE_PREFIX))
        return null;
    const value = Number(trigger.slice(VALUE_PREFIX.length));
    return Number.isFinite(value) ? value : null;
}
/** True if `trigger` is a value trigger pinned to exactly this point. */
export function scaleValueTriggerMatches(trigger, value) {
    return parseScaleValueTrigger(trigger) === value;
}
/**
 * True if a submitted `value` is targeted by `trigger`.
 *
 * One trigger per point and nothing else, which is what every other block does. There used to be
 * "All Incorrect" / "All Neutral" role catch-alls here that could fire ALONGSIDE a point's own
 * trigger; Scale was the only block of the eleven with them, and the shared-message case they
 * covered is what the Default Incorrect state is already for.
 */
export function scaleTriggerMatches(trigger, value) {
    return scaleValueTriggerMatches(trigger, value);
}
/**
 * Scale's `resolveEffectiveOptions`, keyed by value.
 *
 * Same fail-closed contract as Dropdown's and FITB's: on no branch match (missing or invalid
 * equation, or a coverage gap) EVERY point becomes `'neutral'` rather than falling back to its own
 * static role. An unresolvable branch setup must be impossible to answer correctly — silently
 * reusing a still-`'correct'` point nobody reviewed for this particular branch is how a student gets
 * marked right against an answer key the teacher never approved for that case.
 */
/**
 * A follower's effective values for the scenario in force — the scenario twin of
 * `resolveEffectiveValues`, written beside it on purpose.
 *
 * The fail-closed rule lives once in `resolveScenarioRoles`. Passed from here is only Scale's
 * own shape: the value key, which is derived from the NUMBER rather than from an id.
 */
export function resolveScenarioValues(values, branches, scenarioId, 
/** For a row that branches on a comparison of its own — see `rolesForScenarioRow`. */
resolvedVariables = {}, angleMode = 'degrees') {
    return resolveScenarioRoles(values, branches, scenarioId, vs => vs.map(v => ({ ...v, role: 'neutral' })), (vs, roles) => versionRolesBy(vs, roles, v => buildScaleValueTrigger(v.value)), resolvedVariables, angleMode);
}
export function resolveEffectiveValues(values, equation, branches, resolvedVariables, angleMode = 'degrees') {
    if (!branches || branches.length === 0)
        return values;
    const matched = resolveMatchedBranch(equation, branches, resolvedVariables, angleMode);
    if (!matched)
        return values.map(v => ({ ...v, role: 'neutral' }));
    // The SAME merge a version applies, keyed the same way — see `applyScaleVersion`.
    return versionRolesBy(values, matched.roles, v => buildScaleValueTrigger(v.value));
}
/** True when this branch has given no point a correct role, so it can never be answered correctly.
 * A boolean rather than FITB's per-blank list — with one axis there is nothing to enumerate. */
export function scaleBranchMissingCorrect(branch, values) {
    return !values.some(v => branch.roles[buildScaleValueTrigger(v.value)] === 'correct');
}
/**
 * Returns the feedback states that match a student's submitted value.
 *
 * Evaluation sequence:
 * 1. optional mode → any_response, answered or not — no correctness check at all for this mode
 *    (see positiveTriggerForMode in use-input-block-feedback.ts).
 * 2. No selection (graded/survey) → unanswered.
 * 3. survey mode, answered → any_answer.
 * 4. graded mode, answered: the submitted position is looked up in `values`. Role 'correct' →
 *    `correct`, terminal — and since ANY point may be marked correct, a contiguous band of
 *    acceptable answers is expressed by marking each of them. Otherwise the point's own targeted
 *    state fires, falling back to `incorrect` if it has none.
 *
 * There is deliberately no `incomplete_answer` step. It meant "some sub-items answered, not all",
 * which cannot occur with a single item — the `unanswered` branch already covers the only empty case.
 *
 * `resolvedVariables` resolves `conditionalBranches` — optional, and a no-op whenever
 * `blockData.conditionalBranches` is unset. Branches are consulted ONLY in `graded` mode, the same
 * gate every other branching block applies, and when one resolves its OWN feedback fires for every
 * trigger below: a branch changes which value is correct, so its messaging has to be able to differ.
 */
export function evaluateScale(answer, blockData, resolvedVariables = {}, 
/** The leader's variant in force for this attempt — see `evaluateDropdown`'s own note. */
scenarioId) {
    const { value } = answer;
    const { gradingMode, feedback, conditionalEquation, conditionalBranches, scenarioBranches, angleMode } = blockData;
    /* A FOLLOWER'S SCENARIO ROW REPLACES the BLOCK's numeric branch, never joins it. The row's
       OWN nested branching is a different thing — see `rolesForScenarioRow`. */
    const followed = gradingMode === 'graded' && !!scenarioBranches && scenarioBranches.length > 0;
    const scenarioRow = followed ? pickScenarioBranch(scenarioBranches, scenarioId) : null;
    const matchedBranch = !followed && gradingMode === 'graded'
        ? resolveMatchedBranch(conditionalEquation, conditionalBranches, resolvedVariables, angleMode)
        : null;
    const values = gradingMode !== 'graded' ? blockData.values
        : followed ? resolveScenarioValues(blockData.values, scenarioBranches, scenarioId)
            : resolveEffectiveValues(blockData.values, conditionalEquation, conditionalBranches, resolvedVariables, angleMode);
    // Optional chaining, not a plain ternary — a branch saved before `feedback` was added to
    // ConditionalRoleBranch has no such field at all, and falling back to the block's own top-level
    // feedback is safer than crashing on `.states` of `undefined`.
    // Incomplete branch coverage falls back to the top-level set with targeted states STRIPPED, so an
    // unmatched answer always lands on Default Incorrect instead of a leftover targeted state firing
    // terminally on an option that was just declared neutral. See activeFeedbackForBranch.
    const activeFeedback = activeFeedbackForBranch((scenarioRow ?? matchedBranch)?.feedback, feedback, gradingMode === 'graded'
        && (followed || (!!conditionalBranches && conditionalBranches.length > 0)));
    /** The targeted states this rating fires. ONE matcher, used by every grading mode — see
     *  `participationOutcome`. */
    const targetedFor = (v) => (Number.isInteger(v)
        ? activeFeedback.states.filter(st => st.type === 'targeted' && scaleTriggerMatches(st.trigger, v))
        : []);
    if (gradingMode === 'optional') {
        // OPTIONAL is INERT: the catch-all, whatever the student did. See `targetedFiresIn`.
        return optionalOutcome(activeFeedback.states);
    }
    if (!Number.isInteger(value)) {
        const unanswered = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'unanswered');
        return unanswered ? [unanswered] : [];
    }
    if (gradingMode === 'survey') {
        return participationOutcome(activeFeedback.states, 'survey', targetedFor(value));
    }
    const picked = values.find(v => v.value === value);
    if (picked?.role === 'correct') {
        const correct = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'correct');
        return correct ? [correct] : [];
    }
    const matched = picked ? targetedFor(picked.value) : [];
    if (matched.length > 0)
        return matched;
    const incorrect = activeFeedback.states.find(s => s.type === 'default' && s.trigger === 'incorrect');
    return incorrect ? [incorrect] : [];
}
/**
 * A Scale block resolved to one of its versions.
 *
 * Composed from the shared primitives rather than reimplementing the merge: the only Scale-
 * specific facts are WHICH array carries the answer key (`values`, not `options`) and how its
 * roles are keyed (`buildScaleValueTrigger`, because a point has a position rather than an id).
 * Everything else — what a version head replaces, what an absent version means, that this is
 * identity for a block with no bank — is the shared contract.
 *
 * Lives here beside the block's other pure helpers, for the same reason `evaluate()` does.
 */
export function applyScaleVersion(data, versionId) {
    const version = pickVersion(data.versions, versionId);
    if (!version)
        return data;
    return {
        ...applyVersionHead(data, version),
        values: versionRolesBy(data.values, version.roles, v => buildScaleValueTrigger(v.value)),
    };
}
/**
 * The write half for Scale — see `applyScaleVersion` for why only the key differs.
 */
export function writeScaleVersion(current, versionId, next) {
    return writeVersionWithRoles(current, versionId, next, Object.fromEntries(next.values.map(v => [buildScaleValueTrigger(v.value), v.role])));
}
/** Add a version to a Scale block — see `applyScaleVersion` for why only the roles key differs. */
export function addScaleVersion(data, fromVersionId, newId) {
    return addVersionWithRoles(data, fromVersionId, newId, Object.fromEntries(data.values.map(v => [buildScaleValueTrigger(v.value), v.role])));
}
/** Remove one, collapsing the last two back to a plain block through Scale's own apply. */
export function removeScaleVersion(data, versionId) {
    return removeVersionWith(data, versionId, applyScaleVersion);
}
//# sourceMappingURL=scale.js.map